import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { dispatch, RootState } from "../../../../state/configureStore";
import AssignmentState from "./AssignmentState";
import Page from "../../../../interfaces/Page";
import RecordedPage from "../../../../interfaces/RecordedPage";

interface BookScreenState {
  currentPageNumber: number;
  currentQuestionId: string;
  isRecording: boolean;
  isGettingReadyToRecording: boolean;
  isPlayingBackAudio: boolean;
  assignmentState: AssignmentState;
  currentPage: Page;
  currentRecordedPage?: RecordedPage;
  recordingSectionNumber: number;
  loadingError?: string;
}

const initialState: BookScreenState = {
  currentPageNumber: 0,
  currentQuestionId: "",
  isRecording: false,
  isGettingReadyToRecording: false,
  isPlayingBackAudio: false,
  assignmentState: {} as AssignmentState,
  currentPage: {} as Page,
  currentRecordedPage: undefined,
  recordingSectionNumber: 0,
  loadingError: undefined,
};

const bookScreenSlice = createSlice({
  name: "bookScreenState",
  initialState,
  reducers: {
    setPage: (state, action: PayloadAction<number>) => {
      state.currentPageNumber = action.payload;
      state.currentQuestionId = "";

      // Setup the associated objects
      const newCurrentPage = state.assignmentState.pages[state.currentPageNumber];
      if (newCurrentPage) {
        state.currentPage = newCurrentPage;
      }
      state.currentRecordedPage = state.assignmentState.recordedPages.find(
        obj => obj.pageId === state.currentPage.navigation.id,
      );

      // Setup the right recording section number
      if (state.currentRecordedPage) {
        state.recordingSectionNumber = state.currentRecordedPage.section;
      } else {
        state.recordingSectionNumber = 0;
      }
    },
    advanceRecordingSection: state => {
      state.recordingSectionNumber += 1;
    },
    resetRecordingSection: state => {
      state.recordingSectionNumber = 0;
    },
    setError: (state, action: PayloadAction<string>) => {
      state.loadingError = action.payload;
    },
    clearError: state => {
      state.loadingError = undefined;
    },
    setCurrentQuestion: (state, action: PayloadAction<string>) => {
      state.currentQuestionId = action.payload;
      state.currentPageNumber = -1;
    },

    startRecording: state => {
      state.isRecording = true;
      state.isGettingReadyToRecording = false;
    },
    stopRecording: state => {
      state.isRecording = false;
      state.isGettingReadyToRecording = false;
    },
    startGettingReadyToRecord: state => {
      state.isRecording = false;
      state.isGettingReadyToRecording = true;
    },
    startPlayingBackAudio: state => {
      state.isPlayingBackAudio = true;
    },
    stopPlayingBackAudio: state => {
      state.isPlayingBackAudio = false;
    },
    setAssignmentState: (state, action: PayloadAction<AssignmentState>) => {
      state.assignmentState = action.payload;
    },
    setRecordedPages: (state, action: PayloadAction<RecordedPage[]>) => {
      state.assignmentState.recordedPages = action.payload;

      // set the recorded page
      state.currentRecordedPage = state.assignmentState.recordedPages.find(
        obj => obj.pageId === state.currentPage.navigation.id,
      );

      // Setup the right recording section number
      if (state.currentRecordedPage) {
        state.recordingSectionNumber = state.currentRecordedPage.section;
      } else {
        state.recordingSectionNumber = 0;
      }
    },
  },
});

export const setPage = (page: number): void => {
  dispatch(bookScreenSlice.actions.setPage(page));
};

export const setCurrentQuestion = (id: string): void => {
  dispatch(bookScreenSlice.actions.setCurrentQuestion(id));
};

export const startRecording = (): void => {
  dispatch(bookScreenSlice.actions.startRecording());
};

export const stopRecording = (): void => {
  dispatch(bookScreenSlice.actions.stopRecording());
};

export const startGettingReadyToRecord = (): void => {
  dispatch(bookScreenSlice.actions.startGettingReadyToRecord());
};

export const startPlayingBackAudio = (): void => {
  dispatch(bookScreenSlice.actions.startPlayingBackAudio());
};

export const stopPlayingBackAudio = (): void => {
  dispatch(bookScreenSlice.actions.stopPlayingBackAudio());
};

export const advanceRecordingSection = (): void => {
  dispatch(bookScreenSlice.actions.advanceRecordingSection());
};

export const resetRecordingSection = (): void => {
  dispatch(bookScreenSlice.actions.resetRecordingSection());
};

export const setError = (errorString: string): void => {
  dispatch(bookScreenSlice.actions.setError(errorString));
};

export const clearError = (): void => {
  dispatch(bookScreenSlice.actions.clearError());
};

export const setAssignmentState = (assignmentState: AssignmentState) => {
  dispatch(bookScreenSlice.actions.setAssignmentState(assignmentState));
};

export const setRecordedPages = (recordedPages: RecordedPage[]) => {
  dispatch(bookScreenSlice.actions.setRecordedPages(recordedPages));
};

export type { BookScreenState };

export const isRecording = (state: RootState) => state.bookScreenState.isRecording;
export const canRecord = (state: RootState) =>
  !state.bookScreenState.isRecording && !state.bookScreenState.isGettingReadyToRecording;

export default bookScreenSlice.reducer;
